<template>
  <div class="costs__wrapper">
    <NavBar>
      <template #left>
        <BackButton @click="onBack" />
      </template>
      <template #right>
        <ShowMoneyToggleButton />
      </template>
    </NavBar>
    <div v-if="emptyState" class="costs__empty">
      <HelperViewContextual
        icon="EF0040"
        description="Ainda não existem informações de custos disponíveis"
        button-label="Voltar"
        @click="handleEmptyStateButton"
      />
    </div>
    <div v-else class="costs__wrapper">
      <div class="costs__header">
        <Heading class="costs__header--title">Custos</Heading>
        <div class="costs__header--content">
          <SkeletonLoader v-if="isLoadingCards">
            <div class="costs__header--card-loader" />
          </SkeletonLoader>
          <SkeletonLoader v-if="isLoadingCards">
            <div class="costs__header--card-loader" />
          </SkeletonLoader>
          <ContentDisplay
            v-if="cards?.feeCard && !isLoadingCards"
            class="costs__header--fee-display"
            footer-type="link"
            footer-label="Entender modelo"
            @click="goToCostsFAQ"
          >
            <template #content-block-1>
              <div>
                <BaseText size="p5" class="costs__header--display-subtitle">
                  {{ cards.feeCard.title }}
                </BaseText>
                <Heading :level="4" class="costs__header--display-title">
                  <Money>
                    {{ cards.feeCard.value }}
                  </Money>
                </Heading>
              </div>
              <BaseText class="costs__header--display-description" size="p5">
                {{ cards.feeCard.description }}
              </BaseText>
            </template>
          </ContentDisplay>
          <ContentDisplay
            v-if="cards?.cashbackCard && !isLoadingCards"
            :class="[
              'costs__header--cashback-display',
              { disabled: !cards.cashbackCard.button?.enabled }
            ]"
            footer-type="navigation"
            :footer-label="cards.cashbackCard.button?.label"
            @click="goToCashbackPage"
          >
            <template #content-block-1>
              <div>
                <BaseText size="p5" class="costs__header--display-subtitle">
                  {{ cards.cashbackCard.title }}
                </BaseText>
                <Heading :level="4" class="costs__header--display-title">
                  <Money>
                    {{ cards.cashbackCard.value }}
                  </Money>
                </Heading>
              </div>
              <BaseText class="costs__header--display-description" size="p5">
                {{ cards.cashbackCard.description }}
              </BaseText>
            </template>
          </ContentDisplay>
        </div>
      </div>
      <main class="costs">
        <section v-if="!hasError" class="costs__table">
          <div class="costs__table--heading-container">
            <div class="costs__table--heading">
              <Heading :level="3">Histórico</Heading>
              <BaseText size="p5" class="costs__subtitle">
                Pendente inicial + Custos - Pago = Pendente final
              </BaseText>
            </div>
            <FilterSelect
              v-if="availableYears"
              use-option-label
              render-menu-with-portal
              :options="yearsOptions"
              label=""
              :value="selectedYear"
              @change="onChange"
            />
          </div>
          <DataTable
            show-divider
            :has-heading="true"
            :columns="columns"
            :data="summary"
            :status="tableStatus"
            @on-row-click="onRowClick"
          />
        </section>
        <HelperViewStatus
          v-else
          generic-error
          button-label="Tentar novamente"
          @click="fetchCostsList(true)"
        />
        <CostSideSheet
          :is-open="isSideSheetOpen"
          :costs-details="selectedMonth"
          @close="isSideSheetOpen = false"
        />
      </main>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router/composables';

import {
  NavBar,
  Heading,
  DataTable,
  DataTableProps,
  HelperViewStatus,
  BaseText,
  FilterSelect,
  ContentDisplay,
  SkeletonLoader,
  HelperViewContextual
} from '@warrenbrasil/nebraska-web';
import { BackButton } from '@/modules/common/components/buttons/BackButton';
import { ShowMoneyToggleButton } from '@/modules/common/components/buttons/ShowMoneyToggleButton';

import {
  getCostsCards,
  getCostsDetailsV2,
  ICostsCards,
  IMonth
} from '@/modules/cash/services/costs';
import CostSideSheet from '@/modules/cash/views/Costs/components/CostSideSheet/CostSideSheet.vue';
import { Money } from '@/modules/common/components/Money';
import MoneyCell from './components/MoneyCell/MoneyCell.vue';

const router = useRouter();
const hasError = ref(false);
const isLoading = ref(true);
const selectedYear = ref('');
const selectedMonth = ref<IMonth>({} as IMonth);
const isSideSheetOpen = ref(false);
const availableYears = ref<string[] | null>(null);
const summary = ref<IMonth[] | null>(null);
const cards = ref<ICostsCards>({} as ICostsCards);
const isLoadingCards = ref(true);

const tableStatus = computed(() => {
  if (hasError.value) return 'error';
  if (isLoading.value) return 'loading';

  return 'default';
});

const columns: DataTableProps.Column[] = [
  {
    label: 'Mês',
    type: 'chip',
    getter: ({ month }) => ({
      text: month
    })
  },
  {
    label: 'Pendente inicial',
    component: MoneyCell,
    align: DataTableProps.Align.Right,
    getter: 'initialPendingValue'
  },
  {
    label: 'Custos',
    component: MoneyCell,
    align: DataTableProps.Align.Right,
    getter: 'costs'
  },
  {
    label: 'Pago',
    component: MoneyCell,
    align: DataTableProps.Align.Right,
    getter: 'paidValue'
  },
  {
    label: 'Pendente final',
    component: MoneyCell,
    align: DataTableProps.Align.Right,
    getter: 'finalPendingValue'
  },
  {
    label: 'Status',
    component: MoneyCell,
    align: DataTableProps.Align.Right,
    getter: ({ isClosedMonth }: IMonth) => ({
      isChip: true,
      text: isClosedMonth ? 'Fechado' : 'Aberto',
      chipColor: !isClosedMonth ? 'positive' : 'neutral'
    })
  }
];

const yearsOptions = computed(() => {
  return availableYears.value?.map(year => {
    return {
      label: String(year),
      value: String(year)
    };
  });
});

function onBack() {
  router.push({ name: 'home' });
}

function goToCostsFAQ() {
  window.open('https://warren.com.br/nosso-modelo', '_blank');
}

function goToCashbackPage() {
  if (!cards.value.cashbackCard.button?.enabled) return;
  router.push({ name: 'cashback' });
}

async function onChange(value: string) {
  selectedYear.value = value;
  await fetchCostsList();
}

function onRowClick(value: IMonth) {
  selectedMonth.value = value;
  isSideSheetOpen.value = true;
}

function handleEmptyStateButton() {
  router.back();
}

const emptyState = computed(() => {
  const hasNoYears = !availableYears.value || availableYears.value.length === 0;
  const hasNoMonths = !summary.value || summary.value.length === 0;

  return !isLoading.value && !hasError.value && hasNoMonths && hasNoYears;
});

async function fetchCostsList(setSelectedYear = false) {
  hasError.value = false;
  try {
    isLoading.value = true;

    const data = await getCostsDetailsV2(selectedYear.value);

    availableYears.value = data.availableYears;
    summary.value = data.months;

    if (
      setSelectedYear &&
      !selectedYear.value &&
      availableYears.value?.length > 0
    ) {
      selectedYear.value = String(availableYears.value[0]);
    }
  } catch (e) {
    hasError.value = true;
  } finally {
    isLoading.value = false;
  }
}

async function fetchCostsCards() {
  try {
    isLoadingCards.value = true;
    const data = await getCostsCards();

    cards.value = data;
  } finally {
    isLoadingCards.value = false;
  }
}

onMounted(async () => {
  await fetchCostsList(true);
  await fetchCostsCards();
});
</script>

<style lang="less" scoped>
.navbar-container {
  border-bottom: none;
  width: 100%;
  background-color: @background-primary;
}

.skeleton-container {
  width: 100%;
}

.costs {
  width: 100%;
  max-width: 1008px;
  margin: 0 auto;
  display: flex;
  justify-content: center;
  padding: @size-spacing-x1000 @size-spacing-x450;

  &__empty {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: calc(100vh - 56px);
    background: @background-primary;
    width: 100%;
  }

  &__wrapper {
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }

  &__header {
    width: 100%;
    display: flex;
    flex-direction: column;
    background-color: @background-primary;
    justify-content: center;
    align-items: center;
    padding: @size-spacing-x1000 @size-spacing-x450;

    @media screen and (min-width: 1024px) {
      padding: @size-spacing-x1000 0;
    }

    &--card-loader {
      width: 100%;
      height: 239px;
    }

    &--fee-display,
    &--cashback-display {
      :deep(.cell) {
        padding: 0;
        min-width: 55px;
      }

      .md {
        font-size: 16px;
        font-weight: 300;
      }

      .sm {
        color: @element-primary;
      }
    }

    &--cashback-display {
      background-color: @status-positive-background;
      max-width: none;
      width: 100%;

      @media screen and (min-width: 1024px) {
        max-width: 492px;
      }

      :deep(footer .footer-button__inner) {
        background-color: @status-positive-background;
      }
    }

    &--cashback-display.disabled {
      :deep(.button--container:hover) {
        transform: none !important;
      }

      :deep(.base-icon--container) {
        opacity: 0.3;
      }

      :deep(footer .footer-button__inner p) {
        opacity: 0.3;
      }
    }

    &--cashback-display &--display-subtitle {
      color: @status-positive !important;
      font-weight: 300;
    }

    &--cashback-display &--display-title {
      color: @monetary-positive;
      margin-bottom: @size-spacing-x500;
      font-weight: 400;
    }

    &--fee-display &--display-subtitle {
      color: @brand-primary !important;
      font-weight: 300;
    }

    &--display-description {
      font-size: 16px;
    }

    &--fee-display &--display-title {
      color: @status-brand;
      margin-bottom: @size-spacing-x500;
      font-weight: 400;
    }

    &--content {
      display: flex;
      flex-direction: column;
      max-width: 1008px;
      width: 100%;
      gap: @size-spacing-x500;

      @media screen and (min-width: 1024px) {
        flex-direction: row;
      }
    }

    &--title {
      max-width: 857px;
      padding-bottom: @size-spacing-x400;
      width: 100%;

      @media screen and (min-width: 1024px) {
        max-width: 1008px;
      }
    }
  }

  &__table {
    width: 100%;
    margin-bottom: @size-spacing-x800;

    &--heading-container {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    .helper-view {
      margin: auto;
      padding: @size-spacing-x500 0;
    }

    :deep(.show-divider) {
      &:last-child {
        td {
          border-bottom: none;
        }
      }
    }
  }

  :deep(.data-table__heading) {
    padding: @size-spacing-x350 0;
  }
}

:deep(footer .footer-button__inner p) {
  color: @element-primary !important;
  font-weight: 400;
}

:deep(.header-cell.cell) {
  padding: @size-spacing-x500 @size-spacing-x400;
}

:deep(.cell) {
  padding: @size-spacing-x500 @size-spacing-x400;
}
</style>
